/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.22.0
 */

import type { ICommonTranslation } from '@modules/internationalization/types';
import type IPageSubscriberMobileTranslation from '@modules/internationalization/types/pages/subscriberMobile';
import {
    TableCell,
    TableCellBalance,
    TableCellDate,
    TableCellLabel,
    TableCellLink,
    TableCellText,
    TableColumnCheckbox,
    TableFilterLabels,
    TableFilterMultiSelect,
    TableFilterRange,
    TableFilterRangeDatePicker,
    TableFilterSelect,
    TableHeader,
    TTableColumnConfig,
    useTableCellAccount,
    useTableCellCampaign,
    useTableMsisdnCell,
} from '@modules/table';

import { ECampaignsType } from '../../PageCampaign';
import { EFormatData } from '../../PageDashboard/types/widgetTypes';
import { filterMsisdnInputHandler } from '../helpers/filterMsisdnInputHandler';
import { filterMsisdnSearchInputTooltipMsg } from '../helpers/filterMsisdnSearchInputTooltipMsg';
import { filterMsisdnSearchInputType } from '../helpers/filterMsisdnSearchInputType';
import type { IReportsSubscriberTable } from '../types';
import { TableReportsSubscriberCellPersData } from './TableReportsSubscriberCellPersData';

export const TableReportsSubscriberColumns = (
    { table, tableFilter }: IPageSubscriberMobileTranslation,
    commonTranslation: ICommonTranslation,
    isMultiSearchEnabled: boolean,
    msisdnFiltersMaxSize?: number,
): TTableColumnConfig<IReportsSubscriberTable> => {

    const useAccountCell = ({ account }: IReportsSubscriberTable) => useTableCellAccount(account.id, account.number);

    const useAssistantCampaignCell = ({ assistantCampaignsCount, msisdn }: IReportsSubscriberTable) =>
        useTableCellCampaign(msisdn, assistantCampaignsCount, ECampaignsType.subscriber);

    const useMsisdnCell = ({ msisdn, id }: IReportsSubscriberTable) => useTableMsisdnCell(id, msisdn, true);
    const filterMsisdnInputHandlerCallback =
        filterMsisdnInputHandler(isMultiSearchEnabled, msisdnFiltersMaxSize);
    const filterMsisdnSearchInputTypeCallback =
        filterMsisdnSearchInputType(msisdnFiltersMaxSize);
    const filterMsisdnSearchInputTooltipMsgCallback =
        filterMsisdnSearchInputTooltipMsg(msisdnFiltersMaxSize, tableFilter);

    return {
        /* Checkbox */
        checked: TableColumnCheckbox<IReportsSubscriberTable>((row) => ({
            checked: row.checked,
            item: { id: row.id, label: row.msisdn },
        })),
        /* Номер */
        msisdn: {
            accessor: 'msisdn',
            Header: <TableHeader label={table.msisdn.header} name='msisdn'>
                {/* */}
                <TableFilterMultiSelect
                    filterInputHandlerCallback={filterMsisdnInputHandlerCallback}
                    name='msisdn'
                    searchInputMaxLength={msisdnFiltersMaxSize}
                    searchInputTooltipMsgCallback={filterMsisdnSearchInputTooltipMsgCallback}
                    searchInputTypeCallback={filterMsisdnSearchInputTypeCallback}
                />
                {/*  */}
            </TableHeader>,
            sticky: 'left',
            ...TableCellLink<IReportsSubscriberTable>(useMsisdnCell),
        },
        /* ФИО */
        name: {
            accessor: 'name',
            Header: <TableHeader label={table.name.header} name='name'>
                <TableFilterMultiSelect name='name' />
            </TableHeader>,
            ...TableCellText<IReportsSubscriberTable>(({ name }) => name),
        },
         /* Регион */
         zone: {
            accessor: 'zone',
            Header: <TableHeader label={table.zone.header} name='zone'>
                <TableFilterMultiSelect name='zone' />
            </TableHeader>,
            ...TableCellText<IReportsSubscriberTable>(({ zone }) => zone),
        },
        /* Метки */
        labels: {
            accessor: 'labels',
            Header: <TableHeader label={table.labels.header} name='labels'>
                <TableFilterLabels name='labels' />
            </TableHeader>,
            ...TableCellLabel<IReportsSubscriberTable>(({ labels }) => labels ?? []),
        },
        /* Должность */
        position: {
            accessor: 'position',
            Header: <TableHeader label={table.position.header} name='position'>
                <TableFilterMultiSelect name='position' />
            </TableHeader>,
            ...TableCellText<IReportsSubscriberTable>(({ position }) => position),
        },
         /* Персональные данные*/
         persDataStatusName: {
            accessor: 'persDataStatusName',
            Header: <TableHeader label={table.persDataStatusName.header} name='persDataStatusName'>
                <TableFilterMultiSelect name='persDataStatusName' />
            </TableHeader>,
            Cell: props => TableCell(props)({
                render: ({ row }) => <TableReportsSubscriberCellPersData
                    commonTranslation={commonTranslation}
                    row={row}
                />,
            }),
        },
        /* Лицевой счет */
        account: {
            accessor: 'account',
            Header: <TableHeader label={table.account.header} name='account'>
                <TableFilterMultiSelect name='account' />
            </TableHeader>,
            ...TableCellLink<IReportsSubscriberTable>(useAccountCell),
        },
        /* Правила разделения расходов */
        dcRules: {
            accessor: 'dcRules',
            Header: <TableHeader label={table.dcRules.header} name='dcRules'>
                <TableFilterMultiSelect name='dcRules' />
            </TableHeader>,
            ...TableCellText<IReportsSubscriberTable>(({ dcRules }) => dcRules),
        },
        /* Дополнительный городской номер */
        cityNumber: {
            accessor: 'cityNumber',
            Header: <TableHeader label={table.cityNumber.header} name='cityNumber'>
                <TableFilterMultiSelect name='cityNumber' />
            </TableHeader>,
            ...TableCellText<IReportsSubscriberTable>(({ cityNumber }) => cityNumber),
        },
         /* ICC */
         icc: {
            accessor: 'icc',
            Header: <TableHeader label={table.icc.header} name='icc'>
                <TableFilterMultiSelect name='icc' />
            </TableHeader>,
            ...TableCellText<IReportsSubscriberTable>(({ icc }) => icc),
        },
        /* баланс | условный баланс */
        balance: {
            accessor: 'balance',
            Header: <TableHeader label={table.balance.header} name='balance'>
                <TableFilterRange name='balance' />
            </TableHeader>,
            ...TableCellBalance<IReportsSubscriberTable>(({ balance }) => ({
                date: balance?.date,
                value: balance?.value,
                currency: '',
            })),
        },
        /* Тариф */
        ratePlan: {
            accessor: 'ratePlan',
            Header: <TableHeader label={table.ratePlan.header} name='ratePlan'>
                <TableFilterMultiSelect name='ratePlan' />
            </TableHeader>,
            ...TableCellText<IReportsSubscriberTable>(({ ratePlan }) => ratePlan),
        },
         /* Плановая дата блокировки */
         persDataLockDate: {
            accessor: 'persDataLockDate',
            Header: <TableHeader label={table.persDataLockDate.header} name='persDataLockDate'>
                <TableFilterRangeDatePicker name='persDataLockDate' />
            </TableHeader>,
            ...TableCellDate<IReportsSubscriberTable>(({ persDataLockDate }) => ({
                date: persDataLockDate,
                format: EFormatData.FullDateWithTime,
            })),
        },
        /* Предложения */
        assistantCampaignsCount: {
            accessor: 'assistantCampaignsCount',
            Header: <TableHeader label={table.assistantCampaignsCount.header} name='assistantCampaignsCount'>
                <TableFilterSelect name='assistantCampaignsCount' />
            </TableHeader>,
            ...TableCellLink<IReportsSubscriberTable>(useAssistantCampaignCell),
            align: 'right',
        },
        /* Договор */
        contractNumber: {
            accessor: 'contractNumber',
            Header: <TableHeader label={table.contractNumber.header} name='contractNumber'>
                <TableFilterMultiSelect name='contractNumber' />
            </TableHeader>,
            ...TableCellText<IReportsSubscriberTable>(({ contractNumber }) => contractNumber),
        },
        /* Статус */
        status: {
            accessor: 'status',
            Header: <TableHeader label={table.status.header} name='status'>
                <TableFilterMultiSelect name='status' />
            </TableHeader>,
            ...TableCellText<IReportsSubscriberTable>(({ status }) => status),
        },
         /* Тип нумерации */
         numberType: {
            accessor: 'numberType',
            Header: <TableHeader label={table.numberType.header} name='numberType'>
                <TableFilterMultiSelect name='numberType' />
            </TableHeader>,
            ...TableCellText<IReportsSubscriberTable>(({ numberType }) => numberType),
        },
        /* КПП структурного подразделения */
        subdivisionKpp: {
            accessor: 'subdivisionKpp',
            Header: <TableHeader label={table.subdivisionKpp.header} name='subdivisionKpp'>
                <TableFilterMultiSelect name='subdivisionKpp' />
            </TableHeader>,
            ...TableCellText<IReportsSubscriberTable>(({ subdivisionKpp }) => subdivisionKpp),
        },
         /* Название структурного подразделения */
         subdivisionName: {
            accessor: 'subdivisionName',
            Header: <TableHeader label={table.subdivisionName.header} name='subdivisionName'>
                <TableFilterMultiSelect name='subdivisionName' />
            </TableHeader>,
            ...TableCellText<IReportsSubscriberTable>(({ subdivisionName }) => subdivisionName),
        },
         /* Уровень доступа */
         accessName: {
            accessor: 'accessName',
            Header: <TableHeader label={table.accessName.header ?? ''} name='accessName'>
                <TableFilterMultiSelect name='accessName' />
            </TableHeader>,
            ...TableCellText<IReportsSubscriberTable>(({ accessName }) => accessName),
        },
        /* Статус участия */
        loyaltyStatus: {
            accessor: 'loyaltyStatus',
            Header: <TableHeader label={table.loyaltyStatus.header} name='loyaltyStatus'>
                <TableFilterSelect name='loyaltyStatus' />
            </TableHeader>,
            ...TableCellText<IReportsSubscriberTable>(({ loyaltyStatus }) => loyaltyStatus),
        },
    };
};
